// Orbit camera controller for the 3D view. Pure view state (yaw/pitch/distance)
// around a ground target; the target is st.cam.x/y (the look-at point, shared with
// the 2D renderer). No game logic. Owner: client-render3d.

import { PerspectiveCamera, Vector3 } from 'three'
import type { GameState } from '../state'

export interface OrbitCam {
  yaw: number // radians — spin around the vertical axis (Q/E)
  yawTarget: number // eased toward (used for Shift's 60° snaps)
  spinVel: number // rad/s of free spin while Q/E is held (0 = not free-spinning)
  pitch: number // radians above the ground (0 = horizon, π/2 = top-down)
  dist: number // camera distance from the target
}

export const MIN_DIST = 160
export const MAX_DIST = 4200
export const FOV = 50

export function createOrbit(): OrbitCam {
  return { yaw: 0, yawTarget: 0, spinVel: 0, pitch: 0.92, dist: 1100 }
}

/** Angular speed of a free (un-snapped) Q/E spin, radians per second. */
export const SPIN_SPEED = 1.7

/** Ease the yaw toward its target (smooth 60° snaps) — call once per frame. */
export function easeOrbit(o: OrbitCam): void {
  if (o.yaw !== o.yawTarget) {
    o.yaw += (o.yawTarget - o.yaw) * 0.18
    if (Math.abs(o.yawTarget - o.yaw) < 0.0008) o.yaw = o.yawTarget
  }
}

/** Place the THREE camera from the orbit state + ground target. */
export function applyOrbit(cam: PerspectiveCamera, o: OrbitCam, st: GameState): void {
  const tx = st.cam.x, tz = st.cam.y
  const horiz = o.dist * Math.cos(o.pitch)
  cam.position.set(tx + horiz * Math.sin(o.yaw), o.dist * Math.sin(o.pitch), tz + horiz * Math.cos(o.yaw))
  cam.up.set(0, 1, 0)
  cam.lookAt(tx, 0, tz)
  cam.updateMatrixWorld()
}

/** Ground-plane right/forward unit vectors matching the current screen axes — for
 *  grab-panning (drag moves the target so the world follows the cursor). */
export function groundBasis(cam: PerspectiveCamera): { rx: number; rz: number; fx: number; fz: number } {
  const right = new Vector3().setFromMatrixColumn(cam.matrixWorld, 0)
  right.y = 0; right.normalize()
  // screen-up on the ground = camera forward flattened
  const fwd = new Vector3(); cam.getWorldDirection(fwd)
  fwd.y = 0; fwd.normalize()
  return { rx: right.x, rz: right.z, fx: fwd.x, fz: fwd.z }
}
